import { type Ref, computed, unref, useStore } from "@nuxtjs/composition-api";

import { getProductSetsStore } from "@/store/product-sets";
import { filterRemoveUndefined } from "@/utils/transform/array";

/**
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 *
 * @function useProductSet
 * @param    {Ref<string> | string} productSetId
 * @desc     A composable that returns a product set from the product sets store
 *
 * @summary  Composable used to retrieve a product set.
 *
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 */
export const useProductSet = (productSetId: Ref<string> | string) => {
  const store = useStore();

  const productSet = computed(() => getProductSetsStore(store).productSets[unref(productSetId)]);

  return { productSet };
};

/**
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 *
 * @function useProductSetProduct
 * @param    {Ref<string> | string} productSetId
 * @param    {Ref<string> | string} productDetailsId
 * @desc     A composable that returns a product from a product set
 *
 * @summary  Composable used to retrieve a product from a product set.
 *
 ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
 */
export const useProductSetProduct = (productSetId: Ref<string> | string, productDetailsId: Ref<string> | string) => {
  const store = useStore();
  const productSetsStore = getProductSetsStore(store);

  const productSetProduct = computed(() => {
    const productSetProductIds = productSetsStore.productSetFeed(unref(productSetId)).ids;
    const productSetProducts = productSetProductIds
      .map((id) => productSetsStore.productSetProducts[id])
      .filter(filterRemoveUndefined);
    return productSetProducts.find((product) => product.productReferenceId === unref(productDetailsId));
  });

  return { productSetProduct };
};
